import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import clsx from 'clsx';
import { AnimatedSection } from './AnimatedSection';
import { usePublicConfig } from '../context';

const getPlans = (chamaName: string) => [
  {
    name: 'For small chamas',
    price: 'Custom pricing',
    cadence: '10-25 members',
    description: 'Perfect for growing chamas that want to move beyond WhatsApp groups and Excel sheets.',
    features: ['Automated M-Pesa tracking', 'Member dashboards', 'SMS notifications', 'Basic reporting'],
    highlighted: false,
  },
  {
    name: 'Most popular',
    price: 'See it in action',
    cadence: 'like {chamaName} demo',
    description: 'Everything you need to run a professional chama—see the live demo to understand what you get.',
    features: ['Full automation', 'Payout scheduling', 'Penalty management', 'Admin & member portals', 'SMS confirmations', 'Complete audit trail'],
    highlighted: true,
  },
  {
    name: 'For large chamas',
    price: 'Enterprise setup',
    cadence: '50+ members',
    description: 'Advanced features for established chamas managing significant monthly contributions.',
    features: ['Priority support', 'Custom integrations', 'Advanced analytics', 'Multi-admin access', 'API access'],
    highlighted: false,
  },
];

export const Pricing = () => {
  const { config: publicConfig } = usePublicConfig();
  const chamaName = publicConfig?.name ?? 'LoopFund';
  const plans = getPlans(chamaName);

  return (
  <AnimatedSection
    id="pricing"
    className="section-padding"
    delay={0.12}
  >
    <div className="mx-auto flex max-w-6xl flex-col gap-12 text-center">
      <div className="space-y-4">
        <span className="rounded-full border border-brand-200 px-5 py-2 text-xs uppercase tracking-[0.35em] text-brand-700 dark:border-brand-300/40 dark:text-brand-200">
          Get started
        </span>
        <h2 className="font-display text-3xl text-slate-900 md:text-4xl dark:text-white">
          Choose the right plan for your chama
        </h2>
        <p className="mx-auto max-w-2xl text-base text-slate-500 dark:text-slate-300">
          {chamaName} is running on our platform—yours can too. Contact us to discuss pricing and get your chama set up in days, not weeks.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {plans.map((plan, index) => (
          <motion.div
            key={plan.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true, amount: 0.5 }}
            className={clsx(
              'flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-8 text-left shadow-sm dark:border-slate-700 dark:bg-slate-900',
              plan.highlighted &&
                'relative border-brand-400 bg-brand-50 dark:border-brand-300/50 dark:bg-slate-800'
            )}
          >
            {plan.highlighted && (
              <span className="absolute right-6 top-6 rounded-full bg-brand-600 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-white">
                Most loved
              </span>
            )}

            <div className="mb-6 space-y-2">
              <h3 className="font-display text-2xl text-slate-900 dark:text-white">
                {plan.name}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-300">
                {plan.description}
              </p>
            </div>

            <div className="mb-8">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-semibold text-slate-900 dark:text-white">
                  {plan.price}
                </span>
                {plan.cadence ? (
                  <span className="text-sm text-slate-400 dark:text-slate-500">
                    {plan.cadence}
                  </span>
                ) : null}
              </div>
            </div>

            <ul className="mt-auto space-y-3 text-sm text-slate-500 dark:text-slate-300">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-center gap-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-500/10 text-brand-600 dark:text-brand-200">
                    <Check className="h-4 w-4" />
                  </span>
                  {feature}
                </li>
              ))}
            </ul>

            <a
              href={plan.highlighted ? '/apply' : 'tel:+254743269238'}
              className={clsx(
                'mt-8 inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold transition-colors',
                plan.highlighted
                  ? 'bg-brand-600 text-white shadow-sm hover:bg-brand-700'
                  : 'border border-slate-200 text-slate-700 hover:border-brand-500 hover:text-brand-600 dark:border-slate-700 dark:text-slate-200 dark:hover:border-brand-300 dark:hover:text-brand-200'
              )}
            >
              {plan.highlighted ? 'Explore live demo' : 'Call us: 0743 269 238'}
            </a>
          </motion.div>
        ))}
      </div>
    </div>
  </AnimatedSection>
  );
};
